<template>
  <v-layout>
    <v-flex>
      <v-row>
        <v-col cols="12" class="py-0">
          <v-container fluid py-0>
            <v-row align="center">
              <div>
                <h1 class="headline">Agent Groups</h1>
              </div>
              <v-col class="py-0">
                <list-filters
                  :filters="filters"
                  label="Find Agent Group"
                  ref-name="agent_groups_list_filters"
                  @searchParamsChanged="onFiltersChange"
                  :expansionPanel="false"
                ></list-filters>
              </v-col>
              <div>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      v-on="on"
                      fab
                      dark
                      color="primary"
                      @click="createItem"
                      class="ma-2"
                    >
                      <v-icon dark>mdi-plus</v-icon>
                    </v-btn>
                  </template>
                  <span>Create New User Group</span>
                </v-tooltip>
              </div>
            </v-row>
          </v-container>
        </v-col>
      </v-row>
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="$apollo.loading"
        v-bind="pagingOptions"
        :search="search"
        item-key="node.id"
        class="elevation-1"
        :height="gridHeight"
        fixed-header
        hide-default-footer
        :sort-by="sortBy"
        :sort-desc="sortDesc"
      >
        <template v-slot:item.totalCount="{ item }">{{
          item.node.members.totalCount
        }}</template>
        <template v-slot:item.node.notInUse="{ item }">
          <v-simple-checkbox
            v-model="item.node.notInUse"
            disabled
          ></v-simple-checkbox>
        </template>
        <template v-slot:item.actions="{ item }">
          <default-list-actions
            :item="item"
            :hide-view="true"
            @deleteItem="deleteItem($event)"
            @editItem="editItem($event)"
          ></default-list-actions>
        </template>
      </v-data-table>

      <v-dialog
        v-model="agentGroupEditorDialog"
        persistent
        width="1200"
        scrollable
      >
        <v-card>
          <v-card-title class="grey lighten-3">
            <v-row>
              <v-icon class="draggable">mdi-drag</v-icon>
              <v-col class="py-0">{{ editorLabel }}</v-col>
            </v-row>
          </v-card-title>
          <v-card-text>
            <agent-group-editor
              :selectedAgentGroup="selectedAgentGroup"
              :doSaveAgentGroup="doSaveAgentGroup"
              :agentGroupEditorDialog="agentGroupEditorDialog"
              @saved="onSaved($event)"
              @onResetSave="onResetSave"
              @enableSaveButton="enableSaveButton"
              @disableSaveButton="disableSaveButton"
            ></agent-group-editor>
          </v-card-text>
          <v-card-actions class="grey lighten-3">
            <v-spacer></v-spacer>
            <v-btn color="warning" text @click="closeDialog">Cancel</v-btn>
            <v-btn
              color="primary"
              @click="onSaveAgentGroup"
              :disabled="saveButtonDisabled"
              >Save</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-row v-if="debug">
        <code
          >Search:
          {{ agentGroups }}
        </code>
      </v-row>
    </v-flex>
  </v-layout>
</template>

<script>
import {
  AGENT_GROUPS_SEARCH_QUERY,
  AGENT_GROUP_DELETE_MUTATION,
} from "../../../constants/graphql";
import DefaultListActions from "../shared/DefaultListActions";
import AgentGroupEditor from "./AgentGroupEditor";
import ListFilters from "../shared/list-filters/ListFilters";
import { pagingOptions } from "../../../helpers/index";
import { ListMixin } from "../../../mixins/ListMixin";
import listFilters from "./agent-groups-filters";
export default {
  name: "AgentGroupsList",

  props: {
    refName: {
      type: String,
      default: () => "agent-groups-list",
    },
  },

  mixins: [ListMixin],

  components: {
    DefaultListActions,
    AgentGroupEditor,
    ListFilters,
  },

  data() {
    return {
      debug: false,
      pagingOptions,
      search: "",
      searchParams: { not_in_use_eq: false },
      selectedAgentGroup: {},
      agentGroups: {},
      agentGroupEditorDialog: false,
      doSaveAgentGroup: false,
      saveButtonDisabled: true,
      headers: [
        {
          text: "Name",
          value: "node.name",
          name: "name",
        },
        {
          text: "No of users",
          value: "node.members.totalCount",
          name: "noOfUsers",
        },
        {
          text: "Not in Use",
          value: "node.notInUse",
          name: "notInUse",
          align: "center",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          name: "actions",
          align: "center",
        },
      ],
    };
  },

  computed: {
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    filters() {
      return listFilters.map((f) => {
        f.props.outlined = this.$store.getters.localSettings("outlinedInputs");
        return f;
      });
    },
    items() {
      return this.agentGroups ? this.agentGroups.edges : [];
    },
    editorLabel() {
      return this.selectedAgentGroup.id != null
        ? this.selectedAgentGroup.name
        : "New Agent Group";
    },
  },

  methods: {
    onFiltersChange(filters) {
      this.first = 50;
      this.searchParams = { ...filters };
    },
    createItem() {
      this.selectedAgentGroup = {};
      this.agentGroupEditorDialog = true;
    },
    editItem(item) {
      this.selectedAgentGroup = item.node;
      this.agentGroupEditorDialog = true;
    },
    deleteItem(item) {
      this.$apollo
        .mutate({
          mutation: AGENT_GROUP_DELETE_MUTATION,
          variables: {
            agentGroupId: item.node.id,
          },
        })
        .then(this.removeGqlObjectHandler, this.removeGqlObjectErrorHandler);
    },
    removeGqlObjectHandler(result) {
      this.$store.dispatch("showAppSnackbar", { msg: "Agent group removed." });
      if (this.$apollo.queries.agentGroups != null) {
        this.$apollo.queries.agentGroups.refetch();
      }
    },
    removeGqlObjectErrorHandler(err) {
      this.$store.dispatch("showAppSnackbar", {
        msg: err.graphQLErrors[0].message,
        color: "error",
      });
    },
    onSaved(context) {
      if (this.$apollo.queries.agentGroups != null) {
        this.$apollo.queries.agentGroups.refetch();
      }
      this.onResetSave();
      this.agentGroupEditorDialog = false;
      this.selectedAgentGroup = {};
      let msg =
        context == "edit" ? "Agent group updated." : "Agent group created.";
      this.$store.dispatch("showAppSnackbar", { msg });
    },
    onResetSave() {
      this.doSaveAgentGroup = false;
    },
    closeDialog() {
      this.agentGroupEditorDialog = false;
      this.selectedAgentGroup = {};
    },
    onSaveAgentGroup() {
      this.doSaveAgentGroup = true;
    },
    enableSaveButton() {
      this.saveButtonDisabled = false;
    },
    disableSaveButton() {
      this.saveButtonDisabled = true;
    },
  },

  apollo: {
    agentGroups: {
      query: AGENT_GROUPS_SEARCH_QUERY,
      fetchPolicy: "network-only",
      variables() {
        return {
          after: this.after,
          filter: { ...this.searchParams, isAgent: false },
        };
      },
    },
  },
};
</script>

<style scoped></style>



// WEBPACK FOOTER //
// src/components/gt/agents/AgentGroupsList.vue